import { saveAs } from 'file-saver';
import { isMobileDevice, isWx } from '@/lib/device-utils';
import { generateMiniProgramUrlLink } from '@/app/services/wechat';
import { blobValidate } from '@/lib/blob-validate';
import { openUrl } from '@/lib/open-url';
import { toast } from 'sonner';

/**
 * 微信端跳小程序下载页，其他移动端直接打开链接，
 * 桌面端用 file-saver 通过 blob 下载支持自定义文件名。
 */
export async function downloadFile(
  url: string,
  fileName: string,
  fetcher: () => Promise<Blob | null>,
) {
  if (isWx()) {
    if (!url) {
      toast.error('下载链接无效', { position: 'top-center' });
      return;
    }
    try {
      const query = `type=document&data=${encodeURIComponent(
        JSON.stringify({ filename: fileName, fileUrl: url }),
      )}`;
      const res = await generateMiniProgramUrlLink(
        '/pages/download/index',
        query,
      );
      if (res?.data) {
        // 下载链路恢复为同窗口跳转，避免手机微信拦截异步新开页。
        openUrl(document, res.data);
        return;
      }
    } catch (e) {
      console.error('生成小程序链接失败', e);
    }
    toast.info('请在小程序中下载文件', { position: 'top-center' });
    return;
  }
  if (isMobileDevice()) {
    window.open(url, '_blank');
    return;
  }
  const blob = await fetcher();
  if (blob) saveAs(blob, fileName);
}

// SVG转PNG并下载的核心函数
export async function svgToPngDownload(
  svgEl: SVGSVGElement,
  fileName: string,
): Promise<void> {
  const style = document.createElement('style');
  style.innerHTML = `p{margin: 0;}`;
  svgEl.appendChild(style);
  const svgString = new XMLSerializer().serializeToString(svgEl);
  // 创建一个隐藏的canvas元素
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context.');
  }
  const src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;

  const img = new Image();
  img.width = svgEl.clientWidth;
  img.height = svgEl.clientHeight;

  img.onload = () => {
    // 设置canvas尺寸与SVG相同
    canvas.width = img.width;
    canvas.height = img.height;
    canvas.style.width = `${img.width}`;
    canvas.style.height = `${img.height}`;

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    // 将SVG绘制到canvas上
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) {
        throw new Error('Failed to create blob.');
      }
      saveAs(blob, fileName + '.png');
      svgEl.removeChild(style);
    });
  };
  img.onerror = () => {
    throw new Error('Failed to load SVG image.');
  };
  img.src = src;
}

export { blobValidate };
